import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, Location } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSidebar } from '../../contexts/SidebarContext';
import { useProjects } from '../../contexts/ProjectContext';
import { Project, SidebarSection } from '../../types';
import {
  FolderIcon, ChevronRightIcon, CheckCircleIcon, CalendarIcon,
  UsersIcon, BarChartIcon, ClockIcon,
  DashboardIcon, WorkspaceIcon,
} from '../icons/Icons';
import raeLogo from '../../assets/images/RAE-LOGO.png';
import './Sidebar.css';

const ARCHIVED_SIDEBAR_LIMIT = 10;

function getProjectShortName(name: string): string {
  return name
    .replace('Houston Processing Plant Electrical Upgrade', 'HPP Electrical Upgrade')
    .replace('Electrical Upgrade', 'Electrical')
    .replace('Processing Plant', 'Plant');
}

interface ProjectNavItemProps {
  project: Project;
  isOpen: boolean;
  isArchived: boolean;
  onToggle: () => void;
  onNav: (projectId: string, path: string) => void;
  isActivePath: (path: string) => boolean;
  hasSidebarAccess: (section: SidebarSection) => boolean;
  isProjectManager: boolean;
  location: Location;
}

function ProjectNavItem({
  project, isOpen, isArchived, onToggle, onNav,
  isActivePath, hasSidebarAccess, isProjectManager, location,
}: ProjectNavItemProps) {
  return (
    <div className={`nav-department ${isArchived ? 'nav-department--archived' : ''}`}>
      <button
        className={`department-toggle ${isArchived ? 'department-toggle--archived' : ''}`}
        onClick={onToggle}
        title={project.name}
      >
        <FolderIcon size={16} />
        <span>{getProjectShortName(project.name)}</span>
        <ChevronRightIcon size={14} className={`chevron ${isOpen ? 'open' : ''}`} />
      </button>

      {isOpen && (
        <ul className="nav-list">
          {hasSidebarAccess('dashboard') && (
            <li>
              <button
                onClick={() => onNav(project.id, '/')}
                className={`nav-button ${isActivePath('/') && location.pathname === '/' ? 'active' : ''}`}
              >
                <DashboardIcon size={18} />
                <span>Dashboard</span>
              </button>
            </li>
          )}
          {hasSidebarAccess('workspace') && (
            <li>
              <button
                onClick={() => onNav(project.id, '/workspace')}
                className={`nav-button ${isActivePath('/workspace') ? 'active' : ''}`}
              >
                <WorkspaceIcon size={18} />
                <span>Workspace</span>
              </button>
            </li>
          )}
          {hasSidebarAccess('my-tasks') && (
            <li>
              <button
                onClick={() => onNav(project.id, '/my-tasks')}
                className={`nav-button ${isActivePath('/my-tasks') ? 'active' : ''}`}
              >
                <CheckCircleIcon size={18} />
                <span>My Tasks</span>
              </button>
            </li>
          )}
          {hasSidebarAccess('calendar') && isProjectManager && (
            <li>
              <button
                onClick={() => onNav(project.id, '/calendar')}
                className={`nav-button ${isActivePath('/calendar') ? 'active' : ''}`}
              >
                <CalendarIcon size={18} />
                <span>Calendar</span>
              </button>
            </li>
          )}
          {hasSidebarAccess('team') && isProjectManager && (
            <li>
              <button
                onClick={() => onNav(project.id, '/team')}
                className={`nav-button ${isActivePath('/team') ? 'active' : ''}`}
              >
                <UsersIcon size={18} />
                <span>Team</span>
              </button>
            </li>
          )}
          {hasSidebarAccess('reports') && (
            <li>
              <button
                onClick={() => onNav(project.id, '/reports')}
                className={`nav-button ${isActivePath('/reports') ? 'active' : ''}`}
              >
                <BarChartIcon size={18} />
                <span>Reports</span>
              </button>
            </li>
          )}
          {hasSidebarAccess('planning') && (
            <li>
              <button
                onClick={() => onNav(project.id, '/planning')}
                className={`nav-button ${isActivePath('/planning') ? 'active' : ''}`}
              >
                <ClockIcon size={18} />
                <span>Planning</span>
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

export const Sidebar: React.FC = () => {
  const { isSidebarOpen, toggleSidebar } = useSidebar();
  const { hasSidebarAccess, isProjectManager } = useAuth();
  const { projects, activeProject, setActiveProjectId } = useProjects();
  const navigate = useNavigate();
  const location = useLocation();
  const [openProjectId, setOpenProjectId] = useState<string | null>(activeProject?.id || null);
  const [archivedOpen, setArchivedOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 640);

  const activeProjects = projects.filter(p => p.status === 'ACTIVE');
  const archivedProjects = projects.filter(p => p.status !== 'ACTIVE');
  const visibleArchived = archivedProjects.slice(0, ARCHIVED_SIDEBAR_LIMIT);
  const hasMore = archivedProjects.length > ARCHIVED_SIDEBAR_LIMIT;

  useEffect(() => {
    if (activeProject && !openProjectId) {
      setOpenProjectId(activeProject.id);
    }
  }, [activeProject?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const closeOnMobile = useCallback(() => {
    if (isMobile && isSidebarOpen) toggleSidebar();
  }, [isMobile, isSidebarOpen, toggleSidebar]);

  const toggleProject = (projectId: string) => {
    if (openProjectId === projectId) {
      setOpenProjectId(null);
    } else {
      setOpenProjectId(projectId);
      setActiveProjectId(projectId);
    }
  };

  const handleProjectNav = (projectId: string, path: string) => {
    setActiveProjectId(projectId);
    navigate(path);
    closeOnMobile();
  };

  const isActivePath = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {isMobile && isSidebarOpen && (
        <div className="sidebar-overlay" onClick={toggleSidebar} aria-hidden="true" />
      )}
      <div className={`sidebar ${isSidebarOpen ? 'expanded' : 'collapsed'}`}>
        <div className="sidebar-header">
          <div className="sidebar-title-row">
            <button className="sidebar-brand" onClick={toggleSidebar} title={isSidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}>
              <img src={raeLogo} alt="RAE" className="sidebar-brand-logo" />
            </button>
          </div>
        </div>

        <nav className="sidebar-nav">
          {/* Active projects */}
          {activeProjects.map(project => (
            <ProjectNavItem
              key={project.id}
              project={project}
              isOpen={openProjectId === project.id}
              isArchived={false}
              onToggle={() => toggleProject(project.id)}
              onNav={handleProjectNav}
              isActivePath={isActivePath}
              hasSidebarAccess={hasSidebarAccess}
              isProjectManager={isProjectManager}
              location={location}
            />
          ))}

          {/* Archived projects — PM only, only when sidebar expanded */}
          {isProjectManager && archivedProjects.length > 0 && isSidebarOpen && (
            <div className="nav-archived-section">
              <button
                className="nav-archived-toggle"
                onClick={() => setArchivedOpen(prev => !prev)}
                title="Toggle archived projects"
              >
                <span className="nav-archived-label">Archived</span>
                <span className="nav-archived-count">{archivedProjects.length}</span>
                <ChevronRightIcon size={12} className={`chevron ${archivedOpen ? 'open' : ''}`} />
              </button>

              {archivedOpen && (
                <>
                  {visibleArchived.map(project => (
                    <ProjectNavItem
                      key={project.id}
                      project={project}
                      isOpen={openProjectId === project.id}
                      isArchived={true}
                      onToggle={() => toggleProject(project.id)}
                      onNav={handleProjectNav}
                      isActivePath={isActivePath}
                      hasSidebarAccess={hasSidebarAccess}
                      isProjectManager={isProjectManager}
                      location={location}
                    />
                  ))}

                  {hasMore && (
                    <button
                      className="nav-archived-view-all"
                      onClick={() => { navigate('/archived-projects'); closeOnMobile(); }}
                    >
                      View all {archivedProjects.length} archived projects →
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </nav>

      </div>
    </>
  );
};
